import type { TrackingDetails } from '../types/locationTracking';
import { formatDateTime } from './dateTime';
import { formatStayDuration } from './locationTracking';

export type TrackingMarkerKind = 'CHECK_IN' | 'STAY' | 'COMMENT' | 'CHECK_OUT' | 'CURRENT';

export type TrackingMarker = {
  kind: TrackingMarkerKind;
  latitude: number;
  longitude: number;
  label: string;
  title: string;
  detail: string;
};

export type TrackingMapColors = {
  checkIn: string;
  stay: string;
  comment: string;
  checkOut: string;
  current: string;
  route: string;
};

// Leaflet is loaded with Subresource Integrity so a tampered CDN file is refused.
const LEAFLET_VERSION = '1.9.4';
const LEAFLET_CSS_SRI = 'sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=';
const LEAFLET_JS_SRI = 'sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=';

function placeText(address: string | null, latitude: number, longitude: number) {
  return address ?? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

/** Markers for every recorded place, check-in first and check-out last. */
export function buildTrackingMarkers(details: TrackingDetails): TrackingMarker[] {
  const markers: TrackingMarker[] = [];
  const { checkIn, checkOut } = details;
  if (checkIn && checkIn.latitude !== null && checkIn.longitude !== null) {
    markers.push({
      kind: 'CHECK_IN',
      latitude: checkIn.latitude,
      longitude: checkIn.longitude,
      label: 'S',
      title: 'Check-in (start)',
      detail: `${formatDateTime(checkIn.at)} · ${placeText(checkIn.address, checkIn.latitude, checkIn.longitude)}`,
    });
  }
  for (const stay of details.stays) {
    markers.push({
      kind: 'STAY',
      latitude: stay.latitude,
      longitude: stay.longitude,
      label: String(stay.stayNumber),
      title: `Long stay ${stay.stayNumber} · ${formatStayDuration(stay.durationMinutes)}${stay.ongoing ? ' (ongoing)' : ''}`,
      detail: `${formatDateTime(stay.arrivedAt)}${stay.departedAt ? ` – ${formatDateTime(stay.departedAt)}` : ''} · ${placeText(stay.address, stay.latitude, stay.longitude)}`,
    });
  }
  for (const comment of details.comments) {
    if (comment.latitude === null || comment.longitude === null) continue;
    markers.push({
      kind: 'COMMENT',
      latitude: comment.latitude,
      longitude: comment.longitude,
      label: 'C',
      title: comment.comment,
      detail: `${formatDateTime(comment.createdAt)} · ${placeText(comment.address, comment.latitude, comment.longitude)}`,
    });
  }
  if (checkOut && checkOut.latitude !== null && checkOut.longitude !== null) {
    markers.push({
      kind: 'CHECK_OUT',
      latitude: checkOut.latitude,
      longitude: checkOut.longitude,
      label: 'E',
      title: 'Check-out (end)',
      detail: `${formatDateTime(checkOut.at)} · ${placeText(checkOut.address, checkOut.latitude, checkOut.longitude)}`,
    });
  }
  const current = details.active ? details.currentLocation : null;
  if (current && current.latitude !== null && current.longitude !== null) {
    markers.push({
      kind: 'CURRENT',
      latitude: current.latitude,
      longitude: current.longitude,
      label: '●',
      title: 'Current location',
      detail: `${formatDateTime(current.at)} · ${placeText(current.address, current.latitude, current.longitude)}`,
    });
  }
  return markers;
}

/** JSON that is safe to embed inside a <script> element. */
function scriptJson(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}

/**
 * Self-contained Leaflet page showing the route and the distinct markers.
 * Rendered in a WebView on Android/iOS and an iframe on the web. All dynamic
 * text is inserted with textContent, never as HTML.
 */
export function buildTrackingMapHtml(
  details: TrackingDetails,
  colors: TrackingMapColors,
  background: string,
): string {
  const payload = {
    route: details.route.map(point => [point.latitude, point.longitude]),
    markers: buildTrackingMarkers(details),
    colors,
    tileUrl: details.map.tileUrl,
    attribution: details.map.attribution,
  };
  return `<!doctype html>
<html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1">
<link rel="stylesheet" href="https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.css" integrity="${LEAFLET_CSS_SRI}" crossorigin="">
<style>
html,body,#map{height:100%;margin:0;padding:0;background:${background};}
.pin{width:28px;height:28px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);}
.pin span{transform:rotate(45deg);color:#fff;font:700 12px -apple-system,Roboto,sans-serif;}
.popup-title{font:600 13px -apple-system,Roboto,sans-serif;margin-bottom:4px;}
.popup-detail{font:12px -apple-system,Roboto,sans-serif;color:#475569;}
#fallback{display:none;position:absolute;inset:0;align-items:center;justify-content:center;font:14px -apple-system,Roboto,sans-serif;color:#64748b;text-align:center;padding:16px;}
</style></head>
<body><div id="map"></div><div id="fallback">Map could not be loaded. Check your connection.</div>
<script src="https://unpkg.com/leaflet@${LEAFLET_VERSION}/dist/leaflet.js" integrity="${LEAFLET_JS_SRI}" crossorigin=""></script>
<script>
(function(){
  var data = ${scriptJson(payload)};
  if (!window.L) { document.getElementById('fallback').style.display = 'flex'; return; }
  var map = L.map('map', { zoomControl: true, attributionControl: true });
  if (data.tileUrl) L.tileLayer(data.tileUrl, { maxZoom: 19, attribution: data.attribution }).addTo(map);
  var bounds = [];
  if (data.route.length > 1) {
    L.polyline(data.route, { color: data.colors.route, weight: 4, opacity: 0.85 }).addTo(map);
  }
  data.route.forEach(function(p){ bounds.push(p); });
  var tones = { CHECK_IN: data.colors.checkIn, STAY: data.colors.stay, COMMENT: data.colors.comment, CHECK_OUT: data.colors.checkOut, CURRENT: data.colors.current };
  function icon(m, dx) {
    var pin = document.createElement('div');
    pin.className = 'pin';
    pin.style.background = tones[m.kind];
    var label = document.createElement('span');
    label.textContent = m.label;
    pin.appendChild(label);
    return L.divIcon({ html: pin.outerHTML, className: '', iconSize: [28, 28], iconAnchor: [14 - dx, 28], popupAnchor: [dx, -26] });
  }
  var entries = data.markers.map(function(m){
    var popup = document.createElement('div');
    var title = document.createElement('div');
    title.className = 'popup-title';
    title.textContent = m.title;
    var detail = document.createElement('div');
    detail.className = 'popup-detail';
    detail.textContent = m.detail;
    popup.appendChild(title);
    popup.appendChild(detail);
    bounds.push([m.latitude, m.longitude]);
    return { m: m, popup: popup, marker: null };
  });
  // Places on top of each other (check-in inside a stay, comments at a stay) would hide
  // markers; nudge later ones sideways on screen so every marker stays visible.
  function layout() {
    var taken = [];
    entries.forEach(function(e){
      var p = map.latLngToContainerPoint([e.m.latitude, e.m.longitude]);
      var dx = 0;
      while (taken.some(function(t){ return Math.abs(t.x - (p.x + dx)) < 26 && Math.abs(t.y - p.y) < 30; })) dx += 26;
      taken.push({ x: p.x + dx, y: p.y });
      if (e.marker) { e.marker.setIcon(icon(e.m, dx)); return; }
      e.marker = L.marker([e.m.latitude, e.m.longitude], { icon: icon(e.m, dx), title: e.m.title, zIndexOffset: e.m.kind === 'CURRENT' ? 2000 : e.m.kind === 'CHECK_IN' || e.m.kind === 'CHECK_OUT' ? 1000 : 0 }).bindPopup(e.popup).addTo(map);
    });
  }
  if (bounds.length) map.fitBounds(bounds, { padding: [28, 28], maxZoom: 17 });
  else map.setView([0, 0], 2);
  layout();
  map.on('zoomend', layout);
})();
</script></body></html>`;
}
